import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  region_id?: string;
  is_active: boolean;
}

export const useSupabaseAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let requestId = 0;
    let loadedAuthUserId: string | null = null;
    let pendingAuthUserId: string | null = null;

    const applySessionState = async (nextSession: Session | null, opts?: { force?: boolean }) => {
      if (!mounted) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      const authUserId = nextSession?.user?.id ?? null;

      if (!authUserId) {
        loadedAuthUserId = null;
        pendingAuthUserId = null;
        setAdminUser(null);
        setLoading(false);
        return;
      }

      // Skip re-fetch if same user (e.g. TOKEN_REFRESHED, SIGNED_IN with same user)
      // This prevents unmounting the admin dashboard on tab focus / token refresh.
      if (authUserId === loadedAuthUserId && !opts?.force) {
        return;
      }

      // Supabase can emit SIGNED_IN and INITIAL_SESSION back-to-back for the
      // same restored session. Do not let the second event cancel the first
      // admin lookup and briefly publish `loading=false` / `adminUser=null`.
      if (authUserId === pendingAuthUserId && !opts?.force) {
        return;
      }

      pendingAuthUserId = authUserId;
      const currentRequestId = ++requestId;

      // Only show loading state if we don't already have an admin user
      // (initial load or user actually changed).
      setAdminUser((prev) => {
        if (!prev) setLoading(true);
        return prev;
      });

      const nextAdminUser = await checkAdminStatus(authUserId);

      if (!mounted || currentRequestId !== requestId) return;

      loadedAuthUserId = authUserId;
      pendingAuthUserId = null;
      setAdminUser(nextAdminUser);
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        void applySessionState(session);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      void applySessionState(session, { force: true });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkAdminStatus = async (userId: string): Promise<AdminUser | null> => {
    if (!userId || typeof userId !== 'string') {
      console.warn('[auth-provider] Cannot check admin status without a valid user id', { userId });
      return null;
    }
    try {
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('*')
        .eq('auth_user_id', userId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (adminData) {
        ['id', 'email', 'role'].forEach((field) => {
          if (!(adminData as any)?.[field]) console.warn('[auth-provider] Admin row missing field', { field, userId });
        });
        return {
          id: adminData.id,
          email: adminData.email || '',
          full_name: adminData.full_name || '',
          role: adminData.role || 'regional_admin',
          region_id: adminData.region_id || undefined,
          is_active: adminData.is_active
        };
      }

      console.warn('[auth-provider] No active admin row for authenticated user', { userId });
      return null;
    } catch (error) {
      console.log('Error checking admin status:', error);
      return null;
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: metadata
      }
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    setAdminUser(null);
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    });
    return { data, error };
  };

  return {
    user,
    session,
    adminUser,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    isAuthenticated: !!user,
    isAdmin: !!adminUser,
    isGlobalAdmin: adminUser?.role === 'global_admin',
    isPresident: adminUser?.role === 'president',
    isRegionalCoordinator: adminUser?.role === 'regional_admin' || adminUser?.role === 'regional_coordinator'
  };
};